import React, { useCallback, useEffect, useState } from 'react';
import {
  Group,
  Content,
  Icon,
  Title,
  FormBlock,
  Profile,
  Heading,
  Text,
  SubBlock,
  Item,
  Label,
  InputValue,
  FormContent,
  Endblock,
  ProfileView,
  ProfileContent,
  InputDiv,
  Input,
  IconDiv
} from './styles';
import { industryList, teamMember } from '../../../global/row';
import { checkStepOneValidation, createWorkspace } from '../../../services/workspaceService';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import Button from '../../../component/button';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { trackAnalyticActivity } from '../../../services/analyticsService';
import AppLogo from '../../../component/appLogo';
import { VIEW_ANALYTICS } from '../../../global/analyticsConstants';
import { Dropdown, DropdownItem } from '../../../component/dropdown';
import { BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { clearPreviousWorkspaceData } from '../../../services/baseServices';
import { loadInitWorkspaceDetails } from '../../../services/appService';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../../routes/urls';
import { captureException } from '../../../services/logService';
import Image from '../../../component/image';

interface Props {
  gotoNext: () => void;
}

const OnboardingStep1: React.FC<Props> = ({ gotoNext }) => {
  const [isTeamMemberOpen, setIsTeamMemberOpen] = useState(false);
  const [isIndustryOpen, setIsIndustryOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  //mobx store variables
  const { workspaceStore } = rootStore;
  const { workspaceInputDetails } = workspaceStore;

  //use selector state variables
  const { name, industry, teamType } = workspaceInputDetails;
  const [selectedTeamMember, setSelectedTeamMember] = useState<{ label: string; value: string | number } | null>(
    teamType || null
  ); // Track selected item
  const [selectedIndustry, setSelectedIndustry] = useState<{ label: string; value: string | number } | null>(
    typeof industry === 'object' && industry !== null ? industry : null
  );

  // Other variables
  const history = useHistory();
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

  useEffect(() => {
    trackAnalyticActivity(VIEW_ANALYTICS.ONBOARDING_STEP_1);
  }, []);

  const onUpdateInputValue = useCallback(
    <K extends keyof typeof workspaceInputDetails>(propsName: K, value: (typeof workspaceInputDetails)[K]) => {
      rootStore.workspaceStore.updateWorkspaceInputDetail(propsName, value);
    },
    []
  );

  const onSubmit = useCallback(
    async (e: React.SyntheticEvent) => {
      e.preventDefault();
      const payload = {
        name: workspaceInputDetails?.name,
        industry: workspaceInputDetails?.industry?.value,
        teamType: workspaceInputDetails?.teamType?.value
      };
      const isValidate = await checkStepOneValidation(payload);
      if (!isValidate) return;

      try {
        setLoading(true);
        await clearPreviousWorkspaceData();
        rootStore.workspaceStore.clearWorkspaceInputDetail();
        rootStore.workspaceStore.updateWorkspaceInputDetail('users', []);
        const clearData = { email: '', name: '' };
        rootStore.workspaceStore.updateUserItem(clearData, 0);
        const result = await createWorkspace(payload);
        if (result) {
          const response = await loadInitWorkspaceDetails(result?.id);
          if (response && response?.length > 0 && response[0]?.id)
            history.push(ROUTES.PROJECT_TASKS.replace(':id', response[0]?.id));
          else history.push(ROUTES.INBOX);
        }
      } catch (e) {
        captureException(e);
      } finally {
        setLoading(false);
      }
    },
    [
      history,
      workspaceInputDetails?.industry?.value,
      workspaceInputDetails?.name,
      workspaceInputDetails?.teamType?.value
    ]
  );

  const handleSelect = (item: { label: string; value: number | string }, type: 'teamType' | 'industry') => {
    if (type === 'teamType' && typeof item.value === 'number') {
      const teamTypeItem: { label: string; value: number } = { label: item.label, value: item.value };
      setSelectedTeamMember(teamTypeItem);
      onUpdateInputValue('teamType', teamTypeItem);
      setIsTeamMemberOpen(false);
    } else {
      // Ensure industry value is always a string
      const industryItem: { label: string; value: string } = { label: item.label, value: String(item.value) };
      setSelectedIndustry(industryItem);
      onUpdateInputValue('industry', industryItem);
      setIsIndustryOpen(false);
    }
  };

  return (
    <>
      <Group className='windows-onboarding-flow'>
        <Content onSubmit={onSubmit}>
          <Icon>
            <AppLogo height='48' />
          </Icon>
          <Title>Just a few quick things to set up your account…</Title>
          <FormContent>
            <FormBlock>
              <Profile>
                <Icon>
                  <Image imageKey='onBoarding_step1_profile' alt='profile' />
                </Icon>
                <ProfileView>
                  <ProfileContent>
                    <Heading>Hello, {userDetails?.given_name}</Heading>
                  </ProfileContent>
                  <Text>First, tell us a bit about your company.</Text>
                </ProfileView>
              </Profile>

              <SubBlock>
                <Item>
                  <Label>Company Name</Label>
                  <InputValue
                    typeof='text'
                    placeholder='Enter company name'
                    value={name}
                    onChange={(e) => {
                      onUpdateInputValue('name', e.target.value);
                    }}
                  />
                </Item>
                <Item>
                  <Label>Team Member</Label>
                  <Dropdown
                    isMinWidth={438}
                    content={
                      <InputDiv role='button' onClick={() => setIsTeamMemberOpen((prev) => !prev)}>
                        <Input
                          placeholder='Select a team member'
                          value={selectedTeamMember ? selectedTeamMember.label : ''}
                        />
                        <IconDiv>
                          <SVGIcon
                            name='down-arrow-icon'
                            width={ICON_SIZE.LG}
                            height={ICON_SIZE.LG}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-secondary)'
                            className={`angleDown-icon ${isTeamMemberOpen ? 'rotate' : ''}`}
                          />
                        </IconDiv>
                      </InputDiv>
                    }
                    trigger='click'>
                    {teamMember.map((item) => {
                      const isSelected = selectedTeamMember?.value === item.value;
                      return (
                        <DropdownItem
                          key={item.value}
                          label={item.label}
                          onClick={() => handleSelect(item, 'teamType')}
                          isSuffix={
                            isSelected ? (
                              <SVGIcon
                                name='checkbox-icon'
                                width={ICON_SIZE.MD}
                                height={ICON_SIZE.MD}
                                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                              />
                            ) : null
                          }
                        />
                      );
                    })}
                  </Dropdown>
                </Item>
                <Item>
                  <Label>Industry</Label>
                  <Dropdown
                    isMinWidth={438}
                    content={
                      <InputDiv role='button' onClick={() => setIsIndustryOpen((prev) => !prev)}>
                        <Input
                          placeholder='Select an industry'
                          value={selectedIndustry ? selectedIndustry.label : ''}
                        />
                        <IconDiv>
                          <SVGIcon
                            name='down-arrow-icon'
                            width={ICON_SIZE.LG}
                            height={ICON_SIZE.LG}
                            viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                            fill='var(--text-secondary)'
                            className={`angleDown-icon ${isIndustryOpen ? 'rotate' : ''}`}
                          />
                        </IconDiv>
                      </InputDiv>
                    }
                    trigger='click'>
                    {industryList.map((item) => {
                      const isSelected = selectedIndustry?.value === item.value;
                      return (
                        <DropdownItem
                          key={item.value}
                          label={item.label}
                          onClick={() => handleSelect(item, 'industry')}
                          isSuffix={
                            isSelected ? (
                              <SVGIcon
                                name='checkbox-icon'
                                width={ICON_SIZE.MD}
                                height={ICON_SIZE.MD}
                                viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
                              />
                            ) : null
                          }
                        />
                      );
                    })}
                  </Dropdown>
                </Item>
              </SubBlock>
            </FormBlock>
            <Endblock>
              {/* <TextFormat>Step 1 to 2</TextFormat> */}
              <Button title='Get Started' variant={BUTTON_TYPE.PRIMARY} buttonType='submit' isLoading={loading} />
            </Endblock>
          </FormContent>
        </Content>
      </Group>
    </>
  );
};
export default observer(OnboardingStep1);
